import Vue from 'vue'
import { formatFetchParamsFromQuery, formatTeam, formatTeamPayload } from '~/assets/util/formatters'
import { trimSpaces } from '~/assets/util/helpers'

export const state = () => {
	return {
		data: [],
		total: 0,
		selected: null,
		nearBy: {
			data: [],
			total: 0,
			requestedCache: {}
		}
	}
}

const initialState = Object.assign({}, state())

export const getters = {
	selectedTeam ({ selected }) {
		return formatTeam(selected)
	},
	canLoadMore ({ data, total }) {
		return data.length < total
	},
	canLoadMoreNearByTeams ({ nearBy }) {
		return nearBy.data.length < nearBy.total
	}
}

export const actions = {
	async fetchTeams ({ state, commit }, { query, loadMore = false }) {
		try {
			const params = formatFetchParamsFromQuery(query, ['startDate', 'endDate', 'clubId'])
			params.include = 'memberships'
			const { data, included, meta } = await this.$axios.$get('/api/v1/users/me/clubs', { params })
			const teamsFormated = data.clubs.map(formatTeam)

			const teams = teamsFormated.map((team) => {
				const include = included.find(item => item.attributes.clubId === team.id)
				team.role = include.attributes.role
				return team
			})

			commit('setTotal', meta.total)

			if (!state.selected) {
				commit('selectTeam', data.clubs[0])
			}

			loadMore
				? commit('addTeams', teams)
				: commit('setTeams', teams)
		} catch (error) {
			commit('setTeams', [])
			commit('setTotal', 0)
			this.$log.error('[TeamsStore] Error during `fetchTeams` action', error)
		}
	},

	async fetchNearByTeams ({ commit }, { query, loadMore = false }) {
		try {
			const params = formatFetchParamsFromQuery(query, ['startDate', 'endDate'])
			const { data, meta } = await this.$axios.$get('/api/v1/clubs', { params })
			const teams = data.map(formatTeam)

			commit('setNearByTotal', meta.total)

			loadMore
				? commit('addNearByTeams', teams)
				: commit('setNearByTeams', teams)
		} catch (error) {
			commit('setNearByTeams', [])
			commit('setNearByTotal', 0)
			this.$log.error('[TeamsStore] Error during `fetchTeams` action', error)
		}
	},

	async createTeam ({ commit, state, dispatch, rootGetters }, payload) {
		const createTeamData = {
			name: trimSpaces(payload.name),
			sportType: formatTeamPayload(payload)
		}
		if (payload.location && payload.location.uri) {
			createTeamData.location = payload.location
		}

		const { data } = await this.$axios.$post('/api/v1/clubs', createTeamData, { serializeBody: true })
		const team = formatTeam(data)
		const { id, name, sportType: sport } = team
		const newTotal = state.total + 1

		team.role = 'owner'
		commit('addTeam', team)
		commit('selectTeam', data)
		commit('setTotal', newTotal)

		dispatch('analytics/group', { id, name, sport }, { root: true })
	},

	async updateTeam ({ commit, state }, { id, payload }) {
		const updateTeamData = {
			...payload,
			...(payload.name && { name: trimSpaces(payload.name) }),
			...(payload.shortName && { shortName: trimSpaces(payload.shortName) })
		}

		const { data } = await this.$axios.$patch(`/api/v1/clubs/${id}`, updateTeamData)

		this.$log.info('[TeamsStore] Updated `team` data', { updateTeamData, response: data })
		return data
	},

	inviteTeamAdmin ({ commit }, { teamId: id, email }) {
		const payload = { role: 'admin', notifications: [email] }

		return this.$axios.$post(`/api/v1/clubs/${id}/invitations`, payload)
	},

	joinTeam ({ commit }, teamId) {
		return this.$axios.post(`/api/v1/clubs/${teamId}/requests`)
			.then(() => commit('cacheJoinTeamRequest', teamId))
	},

	removeTeam ({ commit, state }, teamId) {
		return this.$axios.$delete(`/api/v1/clubs/${teamId}`)
			.then(() => {
				return teamId
			})
	},

	removeTeamFromStore ({ commit, state }, teamId) {
		const newTotal = state.total - 1

		commit('removeTeam', teamId)
		commit('setTotal', newTotal)
	}
}

export const mutations = {
	RESET_STORE: (state) => {
		Object.assign(state, initialState)
	},
	setTeams (state, teams = []) {
		state.data = teams
	},
	setTotal (state, total = 0) {
		state.total = total
	},
	addTeam (state, team) {
		state.data.push(team)
	},
	addTeams (state, teams) {
		state.data = state.data.concat(teams)
	},
	selectTeam (state, team) {
		state.selected = team
	},
	removeTeam (state, id) {
		const index = state.data.findIndex(el => el.id === id)
		state.data.splice(index, 1)
	},

	// Near by teams mutations
	setNearByTeams (state, teams = []) {
		state.nearBy.data = teams
	},
	setNearByTotal (state, total = 0) {
		state.nearBy.total = total
	},
	addNearByTeams (state, teams) {
		state.nearBy.data = state.nearBy.data.concat(teams)
	},
	cacheJoinTeamRequest (state, teamId) {
		Vue.set(state.nearBy.requestedCache, teamId, true)
	}
}
